/**
 * «Correos» (admin): todo lo que manda el worker, cada uno dibujado con datos
 * de ejemplo en español o inglés. Sólo para mirar.
 *
 * El HTML lo dibuja el worker con la MISMA función que usa al enviar (ver
 * `services/emailPreviews.ts`), así que lo que se ve aquí es lo que llega. Va
 * en un iframe aislado: los estilos del correo no tocan el panel ni al revés.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DashboardPageHeader } from '../DashboardPageHeader'
import { getAdminEmail, listAdminEmails, type AdminEmailPreview } from '../../lib/api'
import { translateServerError, useI18n } from '../../lib/i18n'

const GROUPS: AdminEmailPreview['group'][] = ['orders', 'store', 'bookings', 'forms', 'customers', 'domains', 'account']

export function AdminEmails({ token }: { token: string }) {
  const { t } = useI18n()
  const [lang, setLang] = useState<'es' | 'en'>('es')
  const [list, setList] = useState<AdminEmailPreview[] | null>(null)
  const [selected, setSelected] = useState('')
  const [mail, setMail] = useState<{ subject: string; html: string } | null>(null)
  const [failed, setFailed] = useState('')

  const loadList = useCallback(async () => {
    setFailed('')
    try {
      const loaded = await listAdminEmails(token)
      const rows = Array.isArray(loaded) ? loaded : []
      setList(rows)
      setSelected((current) => current || rows[0]?.id || '')
    } catch (err) {
      setFailed(translateServerError(err, t('adminEmails.loadFailed')))
      setList([])
    }
  }, [token, t])

  useEffect(() => { void loadList() }, [loadList])

  useEffect(() => {
    if (!selected) return
    let alive = true
    setMail(null)
    getAdminEmail(token, selected, lang)
      .then((next) => { if (alive) setMail(next?.html ? next : { subject: '', html: '' }) })
      .catch((err: unknown) => { if (alive) setFailed(translateServerError(err, t('adminEmails.loadFailed'))) })
    return () => { alive = false }
  }, [token, selected, lang, t])

  const grouped = useMemo(() => GROUPS
    .map((group) => ({ group, rows: (list ?? []).filter((row) => row.group === group) }))
    .filter((entry) => entry.rows.length), [list])
  const current = list?.find((row) => row.id === selected)

  return (
    <div className="dash-dashboard-view dash-standard-page">
      <DashboardPageHeader title={t('nav.adminEmails')} subtitle={t('adminEmails.subtitle', { count: list?.length ?? 0 })} />

      {failed ? (
        <div className="dash-inline-msg">
          <span>{failed}</span>
          <div className="dash-inline-msg-actions">
            <button type="button" className="dash-secondary-btn" onClick={() => { void loadList() }}>{t('aiUnmet.retry')}</button>
          </div>
        </div>
      ) : null}

      {!list ? <p className="dash-card-description">{t('aiUnmet.loading')}</p> : !list.length ? (
        failed ? null : <p className="dash-card-description">{t('adminEmails.empty')}</p>
      ) : (
        <div className="dash-admin-emails">
          <nav className="dash-admin-emails-list" aria-label={t('nav.adminEmails')}>
            {grouped.map(({ group, rows }) => (
              <section key={group}>
                <h3>{t(`adminEmails.group.${group}`)}</h3>
                {rows.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    className={row.id === selected ? 'is-active' : ''}
                    aria-current={row.id === selected ? 'true' : undefined}
                    onClick={() => { setSelected(row.id) }}
                  >
                    <strong>{t(`adminEmails.name.${row.id}`)}</strong>
                    <small>{row.subject?.[lang] ?? ''}</small>
                  </button>
                ))}
              </section>
            ))}
          </nav>

          <div className="dash-admin-emails-preview">
            <div className="dash-ai-unmet-toolbar">
              <div className="dash-ai-unmet-segment" role="tablist" aria-label={t('adminEmails.language')}>
                {(['es', 'en'] as const).map((id) => (
                  <button key={id} type="button" role="tab" aria-selected={lang === id} className={lang === id ? 'is-active' : ''} onClick={() => { setLang(id) }}>
                    {id.toUpperCase()}
                  </button>
                ))}
              </div>
              {current ? (
                <span className="dash-admin-emails-meta">
                  {t(`adminEmails.audience.${current.audience}`)} · <code>{current.fn}</code>
                </span>
              ) : null}
            </div>
            {current?.spanishOnly ? <p className="dash-admin-emails-note">{t('adminEmails.spanishOnly')}</p> : null}
            <div className="dash-admin-emails-subject">
              <span>{t('adminEmails.subject')}</span>
              <strong>{mail?.subject ?? '…'}</strong>
            </div>
            {mail ? (
              <iframe
                key={`${selected}-${lang}`}
                className="dash-admin-emails-frame"
                title={mail.subject}
                srcDoc={mail.html}
                sandbox=""
              />
            ) : <div className="dash-admin-emails-frame is-loading" />}
          </div>
        </div>
      )}
    </div>
  )
}
